import { IconButton, Stack, TextField, Typography } from "@mui/material";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import SettingsSelector from "features/TTS/components/SettingsSelector";
import { useTranslation } from "react-i18next";
import PhonemeTextField, { PhonemeTextFieldHandle } from "./PhonemeTextField";
import TTSPlayButton from "./TTSPlayButton";
import PhoneticKeyboard from "./PhoneticKeyboard";
import { useAtomValue } from "jotai";
import { languageOptionsAtom } from "atoms";
import { useRef } from "react";
import { getIpaData, getSampaData } from "shared/utils/tts";
import { getPhonemeTooltipMapping } from "./tooltip-mapping";

interface PronunciationTabTemplateProps {
  isIPA: boolean;
  selectedWord: string;
  userPhoneme: string;
  setPhoneme: React.Dispatch<React.SetStateAction<string>>;
  selectedLanguage: string;
  handleLanguageChange: (value: string) => void;
  handleRegenerateClick: () => void;
}

export default function PronunciationTabTemplate({
  isIPA,
  selectedWord,
  userPhoneme,
  setPhoneme,
  selectedLanguage,
  handleLanguageChange,
  handleRegenerateClick,
}: PronunciationTabTemplateProps): JSX.Element {
  const { t } = useTranslation();

  const languageOptions = useAtomValue(languageOptionsAtom);
  const phonemeTextFieldRef = useRef<PhonemeTextFieldHandle>(null);

  const translatedOptions = languageOptions.map((option) => ({
    value: option.value,
    name: t(`tts.toolbarMenu.languageMenu.${option.value.toLowerCase()}`),
  }));

  return (
    <Stack direction="column" mt={4} gap={3}>
      <Stack direction="row">
        <Stack direction="column" spacing={1} mr={2} sx={{ flex: 1.0 }}>
          <Typography variant="subtitle1">
            {t("tts.toolbarMenu.pronunciationMenu.selectedWord")}
          </Typography>
          <TextField
            disabled
            value={
              selectedWord ||
              t("tts.toolbarMenu.pronunciationMenu.selectedWordPlaceholder")
            }
          />
        </Stack>
        <Stack direction="column" spacing={1} mr={1} sx={{ flex: 0.9 }}>
          <Typography variant="subtitle1">
            {t("tts.toolbarMenu.languageMenu.title")}
          </Typography>
          <SettingsSelector
            id={t("tts.toolbarMenu.languageMenu.title")}
            options={translatedOptions}
            onChange={handleLanguageChange}
            selectedValue={selectedLanguage}
            shouldShowDropdown={false}
            size="medium"
          />
        </Stack>
        <Stack pt={5}>
          <IconButton
            onClick={handleRegenerateClick}
            disabled={selectedWord === ""}
          >
            <AutorenewIcon
              sx={{
                color:
                  selectedWord === "" ? "action.disabled" : "secondary.dark",
                width: 35,
                height: 35,
              }}
            />
          </IconButton>
        </Stack>
      </Stack>
      <Stack direction="column" spacing={1}>
        <Typography variant="subtitle1">
          {t("tts.toolbarMenu.pronunciationMenu.title")}
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <PhonemeTextField
            ref={phonemeTextFieldRef}
            userPhoneme={userPhoneme}
            setPhoneme={setPhoneme}
          />
          <TTSPlayButton
            shouldStopPlayer={false}
            disabled={selectedWord === "" || userPhoneme === ""}
            selectedWord={selectedWord}
            alphabet={isIPA ? "ipa" : "x-sampa"}
            phoneme={userPhoneme}
          />
        </Stack>
      </Stack>
      <Typography variant="subtitle1">
        {t("tts.toolbarMenu.pronunciationMenu.phonemeKeyboardHeader")}
      </Typography>
      <PhoneticKeyboard
        addValue={(newValue) => {
          if (phonemeTextFieldRef.current) {
            phonemeTextFieldRef.current.handleAddPhonemeValue(newValue);
          }
        }}
        data={isIPA ? getIpaData(t) : getSampaData(t)}
        mapping={getPhonemeTooltipMapping(t)}
        isIPA={isIPA}
      />
    </Stack>
  );
}
